import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import {
  ArrowRight, ArrowUpRight, Check, ShieldCheck, Cpu, Cloud, Database,
  Users, Zap, Globe2, Mail, Phone, MapPin,
  Building2, Wrench, X, CalendarDays, Clock, Bot, Layers, Brain, Rocket, Linkedin, Youtube, Instagram
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Social URLs — update YOUTUBE_URL and INSTAGRAM_URL when pages are live
const LINKEDIN_URL = "https://www.linkedin.com/company/techshoreglobal/";
const YOUTUBE_URL = "#";
const INSTAGRAM_URL = "#";
const LOGO_IMG = "https://customer-assets.emergentagent.com/job_chat-interface-91/artifacts/cpx84h35_Techshore%20Logo%20latest.png";

const CYBERABAD_IMG =
  "https://customer-assets.emergentagent.com/job_chat-interface-91/artifacts/rz811o42_image.png";

const CLIENT_LOGOS = [
  { name: "ING", src: "https://customer-assets.emergentagent.com/job_chat-interface-91/artifacts/jzwgrd13_image.png" },
  { name: "KFC", src: "https://customer-assets.emergentagent.com/job_chat-interface-91/artifacts/9t6tqk9f_image.png" },
  { name: "Ampol", src: "https://customer-assets.emergentagent.com/job_chat-interface-91/artifacts/55cecu7c_image.png" },
  { name: "Coates", src: "https://customer-assets.emergentagent.com/job_chat-interface-91/artifacts/l0uf4y2a_image.png" },
  { name: "Perpetual", src: "https://customer-assets.emergentagent.com/job_chat-interface-91/artifacts/k47m843l_image.png" },
];

const TEXT_BRANDS = ["IAG", "Woolworths", "Dan Murphy's", "Ticketek"];

/* ---------- Nav ---------- */
function Nav({ openModal }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [];
  const extLinks = [
    { href: "/services", label: "Services" },
    { href: "/crm-solutions", label: "CRM" },
    { href: "/offshore-teams", label: "Offshore" },
    { href: "/industries", label: "Industries" },
    { href: "/about", label: "About" },
    { href: "/blog", label: "Blog" },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-grey-200"
          : "bg-white/0"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[76px] flex items-center justify-between">
        <a href="#top" className="flex items-center" data-testid="nav-logo">
          <img src={LOGO_IMG} alt="TechShore Global — AI-led offshore teams" className="h-12 w-auto" />
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              className="text-[14px] font-medium text-ink-soft hover:text-coral transition-colors"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </a>
          ))}
          {extLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-[14px] font-medium text-ink-soft hover:text-coral transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="TechShore Global on LinkedIn"
            className="w-9 h-9 rounded-full border border-grey-300 hover:border-coral hover:text-coral flex items-center justify-center text-ink-soft transition"
            data-testid="nav-linkedin"
          >
            <Linkedin size={15} />
          </a>
          <button
            onClick={() => openModal("consultation")}
            className="btn-primary !py-2.5 !px-5 !text-[12.5px]"
            data-testid="nav-book-btn"
          >
            Build your offshore team
          </button>
          <button
            className="lg:hidden p-2 rounded border border-grey-300"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            <div className="w-5 h-0.5 bg-ink mb-1" />
            <div className="w-5 h-0.5 bg-ink mb-1" />
            <div className="w-5 h-0.5 bg-ink" />
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-grey-200 bg-white">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                className="text-[15px] font-medium text-ink py-2"
                data-testid={`nav-mobile-${l.id}`}
              >
                {l.label}
              </a>
            ))}
            {extLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-[15px] font-medium text-ink py-2"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

/* ---------- Hero ---------- */
function Hero({ openModal }) {
  return (
    <section id="top" className="relative bg-white pt-32 lg:pt-40 pb-20 lg:pb-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="eyebrow mb-7" data-testid="hero-eyebrow">
            AI-Led Offshore Engineering Partner for Australian Enterprises
          </div>
          <h1
            className="text-[44px] sm:text-[60px] lg:text-[80px] leading-[1.0] tracking-[-0.035em] text-ink font-semibold"
            data-testid="hero-headline"
          >
            <span className="text-coral">AI-led</span> offshore <span className="text-navy">CRM</span> & engineering teams for Australia.
          </h1>
          <p className="mt-8 text-[18px] lg:text-[19px] text-grey-700 max-w-2xl leading-[1.65] font-light" data-testid="hero-sub">
            Scale faster with secure, cost-effective technology teams from our offshore delivery centre in Hyderabad — backed by Australian leadership and enterprise-grade delivery practices.
          </p>

          <div className="mt-10 flex flex-wrap gap-4" data-testid="hero-ctas">
            <button onClick={() => openModal("consultation")} className="btn-primary" data-testid="hero-book-btn">
              Build your offshore team <ArrowRight size={16} />
            </button>
            <button onClick={() => openModal("pricing")} className="btn-outline" data-testid="hero-pricing-btn">
              Get pricing <ArrowUpRight size={16} />
            </button>
          </div>

          <div className="mt-14 flex flex-wrap items-center gap-x-9 gap-y-4 text-grey-600 text-[12.5px] font-semibold uppercase tracking-[0.14em]">
            <div className="flex items-center gap-2.5" data-testid="hero-trust-1">
              <ShieldCheck size={14} className="text-coral" />
              <span>ISO 27001 certified</span>
            </div>
            <div className="flex items-center gap-2.5" data-testid="hero-trust-2">
              <span className="live-dot" />
              <span>10+ yrs serving AU clients</span>
            </div>
            <div className="flex items-center gap-2.5" data-testid="hero-trust-3">
              <Zap size={14} className="text-coral" />
              <span>Build teams in 2–4 weeks</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="hero-img-card">
            <img
              src={CYBERABAD_IMG}
              alt="Cyberabad Hyderabad — TechShore Global offshore IT delivery centre for Australia"
              className="w-full h-auto block"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Trust bar (logos + sectors) ---------- */
function TrustBar() {
  const all = [
    ...CLIENT_LOGOS.map((l) => ({ type: "img", ...l })),
    ...TEXT_BRANDS.map((n) => ({ type: "text", name: n })),
  ];
  const loop = [...all, ...all];

  const sectors = [
    { name: "Banking", icon: <Building2 size={18} /> },
    { name: "Insurance", icon: <ShieldCheck size={18} /> },
    { name: "Retail", icon: <Layers size={18} /> },
    { name: "Energy", icon: <Zap size={18} /> },
    { name: "Logistics", icon: <Globe2 size={18} /> },
    { name: "Entertainment", icon: <Rocket size={18} /> },
  ];

  return (
    <section className="bg-paper py-16 border-y border-grey-200" data-testid="trust-bar">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <div className="text-[12px] uppercase tracking-[0.22em] text-coral font-bold mb-3">
            Enterprise experience
          </div>
          <h2 className="text-[24px] lg:text-[32px] text-ink font-semibold tracking-[-0.02em] max-w-3xl mx-auto leading-tight">
            Experience supporting leading Australian organisations
          </h2>
          <p className="mt-3 text-[14.5px] text-grey-600 font-light max-w-2xl mx-auto">
            Our teams have contributed to enterprise programs across financial services, retail, energy and beyond.
          </p>
        </div>

        {/* Logo marquee */}
        <div className="marquee mb-12" data-testid="trust-logos">
          <div className="marquee-track">
            {loop.map((b, i) => (
              <div key={i} className="flex items-center justify-center min-w-[160px] h-14">
                {b.type === "img" ? (
                  <img
                    src={b.src}
                    alt={`${b.name} — TechShore Global client (offshore IT services Australia)`}
                    className="max-h-12 max-w-[140px] object-contain grayscale opacity-65 hover:opacity-100 hover:grayscale-0 transition"
                  />
                ) : (
                  <span className="text-[24px] text-grey-700 hover:text-ink font-bold tracking-tight transition">
                    {b.name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Sector pills */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 max-w-5xl mx-auto">
          {sectors.map((s) => (
            <div
              key={s.name}
              className="flex items-center gap-3 px-4 py-3.5 bg-white border border-grey-200 rounded-xl hover:border-coral hover:shadow-sm transition"
              data-testid={`sector-${s.name.toLowerCase()}`}
            >
              <span className="text-coral">{s.icon}</span>
              <span className="text-[13.5px] text-ink font-semibold">{s.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- CRM Specialisation ---------- */
function CRMSpecialisation() {
  const platforms = [
    { name: "Salesforce", desc: "Sales, Service, Marketing & Experience Cloud" },
    { name: "Microsoft Dynamics", desc: "365 Sales, Customer Service, Finance & Operations" },
    { name: "SAP", desc: "C/4HANA, S/4HANA CRM modules" },
    { name: "HubSpot", desc: "Sales, Marketing, Service Hub end-to-end" },
    { name: "Zoho", desc: "Zoho CRM, Zoho One suite implementation" },
    { name: "Odoo", desc: "Open-source ERP & CRM customisation" },
  ];
  return (
    <section className="section bg-navy-50" data-testid="crm-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-6">CRM Specialisation</div>
            <h2 className="text-[40px] lg:text-[60px] leading-[1.02] tracking-[-0.03em] text-ink font-semibold">
              Deep CRM expertise across every leading platform.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[16.5px] text-grey-700 leading-[1.65] max-w-md font-light">
              We specialise in supporting, developing and implementing the world's leading CRM platforms — with certified consultants and offshore delivery teams that scale with your roadmap.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="crm-grid">
          {platforms.map((p) => (
            <div key={p.name} className="card p-7 group" data-testid={`crm-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="text-[22px] text-ink font-semibold tracking-[-0.01em]">{p.name}</div>
                <span className="text-[11px] uppercase tracking-[0.16em] text-coral font-bold">CRM</span>
              </div>
              <p className="text-[14.5px] text-grey-700 leading-[1.6] font-light">{p.desc}</p>
              <div className="mt-5 pt-4 border-t border-grey-200 flex items-center justify-between">
                <span className="text-[12px] uppercase tracking-[0.14em] text-navy-2 font-semibold">Implementation · Support · Optimisation</span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a href="/crm-solutions" className="ulink" data-testid="crm-cta">Explore CRM solutions</a>
        </div>
      </div>
    </section>
  );
}

/* ---------- Industries ---------- */
function Industries() {
  const industries = [
    { name: "Insurance", icon: <ShieldCheck size={20} /> },
    { name: "Banking & Financial Services", icon: <Building2 size={20} /> },
    { name: "Retail & eCommerce", icon: <Layers size={20} /> },
    { name: "Hospitality", icon: <Users size={20} /> },
    { name: "Energy & Utilities", icon: <Zap size={20} /> },
    { name: "Logistics & Supply Chain", icon: <Globe2 size={20} /> },
    { name: "Manufacturing", icon: <Wrench size={20} /> },
    { name: "Not-for-Profit", icon: <Rocket size={20} /> },
  ];
  return (
    <section id="industries" className="section bg-white" data-testid="industries-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-6">Industries we serve</div>
            <h2 className="text-[40px] lg:text-[60px] leading-[1.02] tracking-[-0.03em] text-ink font-semibold">
              Trusted by enterprises across Australia & New Zealand.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[16.5px] text-grey-700 leading-[1.65] max-w-md font-light">
              We support organisations of all sizes across regulated, customer-facing and mission-critical industries.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="industries-grid">
          {industries.map((ind) => (
            <div
              key={ind.name}
              className="flex items-center gap-3 px-5 py-5 bg-paper border border-grey-200 rounded-xl hover:border-coral hover:bg-white hover:shadow-sm transition"
              data-testid={`industry-${ind.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            >
              <span className="text-coral">{ind.icon}</span>
              <span className="text-[14px] text-ink font-semibold leading-tight">{ind.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Core Services (4-card services grid) ---------- */
function CoreServices({ openModal }) {
  const services = [
    {
      icon: <Users size={22} />,
      title: "Dedicated Offshore Teams",
      desc: "Senior engineers, QA, DevOps and product talent — embedded in your stack and processes. Pre-vetted, fully managed, and aligned with your roadmap from day one.",
      tag: "Most popular",
    },
    {
      icon: <Brain size={22} />,
      title: "AI & Data Engineering",
      desc: "GenAI, LLMs, agents, RAG pipelines, ML platforms and modern data infrastructure. Production-grade AI engineers who ship features, not prototypes.",
      tag: "Our specialty",
    },
    {
      icon: <Cloud size={22} />,
      title: "Salesforce Support",
      desc: "End-to-end Salesforce expertise — custom development, testing, ongoing support, integrations and migrations. Certified consultants for AU enterprise programs.",
    },
    {
      icon: <Wrench size={22} />,
      title: "Managed Services",
      desc: "Application support, maintenance, upgrades and 24/7 production cover under SLA-based delivery. Free your team to focus on what's next.",
    },
  ];

  return (
    <section id="services" className="section bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-6">What we deliver</div>
            <h2 className="text-[40px] lg:text-[60px] leading-[1.02] tracking-[-0.03em] text-ink font-semibold">
              Four ways we power your engineering org.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[16.5px] text-grey-700 leading-[1.65] max-w-md font-light">
              Pick one capability or combine several into a complete offshore delivery model.
              Every engagement is led by senior AU-aligned leadership.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {services.map((s, i) => (
            <div key={i} className="card p-8 lg:p-9 group flex flex-col" data-testid={`service-${i}`}>
              <div className="flex items-start justify-between mb-6">
                <div className="w-12 h-12 rounded-xl bg-coral-tint text-coral flex items-center justify-center">
                  {s.icon}
                </div>
                {s.tag && (
                  <span className="text-[10.5px] uppercase tracking-[0.16em] text-coral font-bold px-3 py-1.5 bg-coral-tint rounded-full">
                    {s.tag}
                  </span>
                )}
              </div>
              <h3 className="text-[24px] leading-[1.15] tracking-[-0.02em] text-ink font-semibold">
                {s.title}
              </h3>
              <p className="mt-4 text-[15px] text-grey-700 leading-[1.7] flex-1 font-light">{s.desc}</p>
              <div className="mt-7 pt-5 border-t border-grey-200 flex items-center justify-between">
                <button
                  onClick={() => openModal("consultation")}
                  className="text-[12.5px] uppercase tracking-[0.14em] text-ink font-bold hover:text-coral transition"
                  data-testid={`service-cta-${i}`}
                >
                  Discuss this →
                </button>
                <span className="text-[11px] uppercase tracking-[0.14em] text-grey-500 font-semibold">0{i + 1} / 04</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Mid-page CTA banner ---------- */
function MidCTA({ openModal }) {
  return (
    <section className="py-20 lg:py-24 bg-coral-tint border-y border-coral-soft" data-testid="mid-cta">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-8">
          <div className="text-[12px] uppercase tracking-[0.22em] text-coral font-bold mb-3">
            Ready when you are
          </div>
          <h2 className="text-[32px] lg:text-[44px] leading-[1.1] tracking-[-0.025em] text-ink font-semibold">
            Build your offshore team in 2–4 weeks.
          </h2>
          <p className="mt-4 text-[16px] text-grey-700 max-w-2xl leading-[1.65] font-light">
            Free 30-minute consultation. Pre-vetted candidates within days. Fully managed delivery from Hyderabad.
          </p>
        </div>
        <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3" data-testid="mid-cta-buttons">
          <button onClick={() => openModal("consultation")} className="btn-primary justify-center" data-testid="mid-cta-book">
            Build your offshore team <ArrowRight size={16} />
          </button>
          <button onClick={() => openModal("pricing")} className="btn-outline justify-center" data-testid="mid-cta-pricing">
            Get offshore team pricing <ArrowUpRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---------- Why / Proof ---------- */
function ProofStrip() {
  const stats = [
    { num: "10+", label: "Years delivering for Australia" },
    { num: "100+", label: "Engineers placed offshore" },
    { num: "ISO", label: "27001 certified centre" },
    { num: "<11%", label: "Engineering attrition" },
  ];
  return (
    <section className="section bg-white" data-testid="proof-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-6">Why TechShore Global</div>
            <h2 className="text-[40px] lg:text-[60px] leading-[1.02] tracking-[-0.03em] text-ink font-semibold">
              Offshoring built for the AI era.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[16.5px] text-grey-700 leading-[1.7] max-w-md font-light">
              We're not a staffing agency. We build long-term offshore engineering teams that ship
              production AI — fully managed in Hyderabad, fully aligned with your Australian roadmap.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-grey-200 border border-grey-200 rounded-2xl overflow-hidden">
          {stats.map((s, i) => (
            <div key={i} className="bg-white p-8 lg:p-10" data-testid={`proof-stat-${i}`}>
              <div className="stat-num">{s.num}</div>
              <div className="text-[13px] uppercase tracking-[0.14em] text-grey-600 mt-4 leading-snug font-semibold">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Approach ---------- */
function Approach() {
  const steps = [
    {
      n: "01",
      title: "We hire AI-ready engineers",
      desc: "Rigorous 7-step process tailored for AI/ML, data, full-stack and DevOps roles. We screen for ML fundamentals, GenAI fluency, and production engineering — you only see the top 3%.",
      img: "https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?auto=format&fit=crop&w=1400&q=80",
    },
    {
      n: "02",
      title: "We embed them into your stack",
      desc: "Your team works in your tools, your repos, your standups, your SDLC. We handle HR, payroll, compliance, security and infrastructure — so you focus only on shipping.",
      img: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1400&q=80",
    },
    {
      n: "03",
      title: "We keep them committed long-term",
      desc: "Under 11% attrition. We invest in continuous AI/cloud upskilling, career progression, and a culture engineers want to stay in — so your team compounds expertise instead of resetting.",
      img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1400&q=80",
    },
  ];

  return (
    <section id="approach" className="section bg-paper" data-testid="approach-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-6">Our approach</div>
            <h2 className="text-[40px] lg:text-[60px] leading-[1.02] tracking-[-0.03em] text-ink font-semibold">
              Offshore teams, built and run the right way.
            </h2>
          </div>
        </div>

        <div className="space-y-px bg-grey-200">
          {steps.map((s, i) => (
            <div
              key={i}
              className="bg-paper grid lg:grid-cols-12 gap-8 items-center py-12 lg:py-16"
              data-testid={`approach-step-${i}`}
            >
              <div className="lg:col-span-1 px-6 lg:px-0 lg:pl-2">
                <div className="outline-num">{s.n}</div>
              </div>
              <div className="lg:col-span-6 px-6 lg:px-0">
                <h3 className="text-[28px] lg:text-[34px] leading-[1.1] tracking-[-0.02em] text-ink font-semibold max-w-md">
                  {s.title}
                </h3>
                <p className="mt-5 text-[16px] text-grey-700 leading-[1.7] max-w-md font-light">{s.desc}</p>
              </div>
              <div className="lg:col-span-5 px-6 lg:pr-0">
                <div className="aspect-[5/4] rounded-2xl overflow-hidden bg-grey-100">
                  <img src={s.img} alt={`${s.title} — offshore engineering teams Australia`} className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Engagement models ---------- */
function EngagementModels() {
  const models = [
    {
      label: "Team extension",
      title: "Extend your existing team",
      desc: "Augment your current engineering org with senior offshore engineers. Same workflows, same standups, same Jira — just more capacity.",
      best: "Best for: filling skill gaps",
      img: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1200&q=80",
    },
    {
      label: "Dedicated team",
      title: "Build a dedicated team",
      desc: "A complete, integrated offshore squad — engineers, QA, DevOps, AI specialists — owning a product line or workstream end-to-end.",
      best: "Best for: new product lines",
      img: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1200&q=80",
    },
    {
      label: "AI Capability Centre",
      title: "Set up an AI Capability Centre",
      desc: "Your own AI/Data engineering hub of 25–100+ engineers in Hyderabad — driving GenAI, agents, ML platforms and data products at scale.",
      best: "Best for: AI transformation",
      img: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1200&q=80",
    },
  ];

  return (
    <section id="models" className="section bg-white" data-testid="models-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="eyebrow mb-6">Engagement models</div>
            <h2 className="text-[40px] lg:text-[60px] leading-[1.02] tracking-[-0.03em] text-ink font-semibold max-w-3xl">
              However you need to grow, we make it happen.
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {models.map((m, i) => (
            <div key={i} className="card overflow-hidden flex flex-col group" data-testid={`model-card-${i}`}>
              <div className="aspect-[4/3] overflow-hidden bg-grey-100">
                <img src={m.img} alt={`${m.title} — offshore IT services Australia`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
              </div>
              <div className="p-7 flex-1 flex flex-col">
                <div className="text-[11px] uppercase tracking-[0.2em] text-coral font-bold mb-3">{m.label}</div>
                <h3 className="text-[24px] leading-[1.15] tracking-[-0.02em] text-ink font-semibold">{m.title}</h3>
                <p className="mt-4 text-[15px] text-grey-700 leading-[1.65] flex-1 font-light">{m.desc}</p>
                <div className="mt-6 pt-5 border-t border-grey-200 flex items-center justify-between">
                  <span className="text-[12px] uppercase tracking-[0.14em] text-grey-600 font-semibold">{m.best}</span>
                  <span className="card-arrow"><ArrowUpRight size={16} className="text-ink" /></span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- AI Capability ---------- */
function AICapability() {
  const items = [
    { icon: <Brain size={22} />, title: "GenAI & LLM Engineering", desc: "RAG, fine-tuning, evals, agentic workflows, multimodal pipelines — production grade." },
    { icon: <Bot size={22} />, title: "AI Agents & Automation", desc: "Custom agents that integrate with your tools — sales, support, ops automation that ships." },
    { icon: <Database size={22} />, title: "Data Engineering", desc: "Modern lakehouse, dbt, real-time pipelines, vector DBs and ML platform infrastructure." },
    { icon: <Cpu size={22} />, title: "ML Platform & MLOps", desc: "Model lifecycle, feature stores, drift monitoring, scalable inference on AWS/GCP/Azure." },
    { icon: <Layers size={22} />, title: "Full-stack Engineering", desc: "Senior product engineers shipping React, Node, Python, .NET and Salesforce surfaces." },
    { icon: <Cloud size={22} />, title: "Cloud & DevOps", desc: "AWS/Azure/GCP architecture, IaC, Kubernetes, observability, FinOps and security." },
  ];

  return (
    <section id="ai" className="section bg-paper" data-testid="ai-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-6">AI Capability</div>
            <h2 className="text-[40px] lg:text-[60px] leading-[1.02] tracking-[-0.03em] text-ink font-semibold">
              Engineers fluent in production AI.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[16.5px] text-grey-700 leading-[1.65] max-w-md font-light">
              Every engineer we place is screened for AI fluency — comfortable building with LLMs,
              agents, vector stores, and modern data infrastructure from day one.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-grey-200 border border-grey-200 rounded-2xl overflow-hidden">
          {items.map((it, i) => (
            <div key={i} className="bg-paper p-8 hover:bg-white transition-colors" data-testid={`ai-card-${i}`}>
              <div className="w-11 h-11 rounded-lg bg-coral-tint text-coral flex items-center justify-center mb-6">
                {it.icon}
              </div>
              <h3 className="text-[20px] leading-[1.2] tracking-[-0.01em] text-ink font-semibold">{it.title}</h3>
              <p className="mt-3 text-[14.5px] text-grey-700 leading-[1.65] font-light">{it.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap gap-2">
          {["GenAI", "LLMs", "RAG", "LangChain", "PyTorch", "TensorFlow", "dbt", "Snowflake", "Databricks", "Kubernetes", "AWS", "GCP", "Azure", "Salesforce"].map((s) => (
            <span key={s} className="px-3.5 py-2 rounded-full border border-grey-300 bg-white text-[12.5px] text-grey-700 font-medium">
              {s}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Results / Testimonials ---------- */
function Results() {
  const stats = [
    { num: "<11%", label: "Attrition rate" },
    { num: "37%", label: "Average savings" },
    { num: "2–4 wk", label: "To onboard" },
    { num: "100%", label: "AU timezone overlap" },
  ];

  const quotes = [
    {
      quote:
        "We didn't just get engineers — we got a team that genuinely understood our product and shipped AI features faster than our local crew.",
      name: "Head of Engineering",
      role: "Australian fintech",
    },
    {
      quote:
        "TechShore handled everything on the ground in Hyderabad. We just plugged in. 18 months in, zero attrition, two products launched.",
      name: "VP Product",
      role: "ASX-listed retailer",
    },
  ];

  return (
    <section id="results" className="section bg-white" data-testid="results-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-14">
          <div className="eyebrow mb-6">Results we deliver</div>
          <h2 className="text-[40px] lg:text-[60px] leading-[1.02] tracking-[-0.03em] text-ink font-semibold max-w-3xl">
            Proof, not promises.
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-grey-200 border border-grey-200 rounded-2xl overflow-hidden mb-16">
          {stats.map((s, i) => (
            <div key={i} className="bg-white p-8 lg:p-10" data-testid={`result-stat-${i}`}>
              <div className="stat-num">{s.num}</div>
              <div className="text-[13px] uppercase tracking-[0.14em] text-grey-600 mt-4 leading-snug font-semibold">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {quotes.map((q, i) => (
            <div key={i} className="card-flat p-8 lg:p-10" data-testid={`quote-${i}`}>
              <div className="text-coral text-[60px] leading-none font-semibold">"</div>
              <p className="text-[20px] lg:text-[24px] leading-[1.4] tracking-[-0.01em] text-ink font-light -mt-4">
                {q.quote}
              </p>
              <div className="mt-7 pt-6 border-t border-grey-200">
                <div className="font-semibold text-ink text-[15px]">{q.name}</div>
                <div className="text-[12.5px] text-grey-500 uppercase tracking-[0.12em] mt-1 font-semibold">{q.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Cyberabad showcase ---------- */
function CyberabadShowcase() {
  const points = [
    "800K+ tech professionals in Hyderabad",
    "Home to global R&D centres for Microsoft, Amazon, Google",
    "Top-tier AI / Data / Cloud / Salesforce talent",
    "English-fluent, multicultural workforce",
    "Faster hiring cycles than local AU market",
  ];

  return (
    <section className="section bg-paper-2" data-testid="cyberabad-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="hero-img-card">
            <img
              src={CYBERABAD_IMG}
              alt="Cyberabad — Hyderabad's IT corridor at sunset"
              className="w-full h-auto block"
            />
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="eyebrow mb-6">Delivering from</div>
          <h2 className="text-[40px] lg:text-[60px] leading-[1.02] tracking-[-0.03em] text-ink font-semibold">
            Cyberabad — the Silicon Valley of Asia.
          </h2>
          <p className="mt-7 text-[16.5px] text-grey-700 leading-[1.7] max-w-xl font-light">
            Our delivery centre sits in the heart of Hyderabad's Cyberabad tech corridor — home to
            R&D hubs of the world's leading technology companies. We tap directly into that talent
            pool to build your team.
          </p>
          <ul className="mt-10 space-y-4">
            {points.map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-[15.5px]" data-testid={`hyd-point-${i}`}>
                <span className="tick"><Check size={12} strokeWidth={3} /></span>
                <span className="text-grey-700 font-light">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ---------- Security ---------- */
function Security() {
  const items = [
    { icon: <ShieldCheck size={20} />, label: "ISO/IEC 27001:2022", desc: "Independently audited information security management." },
    { icon: <Database size={20} />, label: "Secure infrastructure", desc: "Hardened endpoints, VLAN isolation, encrypted at rest & transit." },
    { icon: <Building2 size={20} />, label: "AU data alignment", desc: "Data protection aligned with Australian regulatory expectations." },
    { icon: <Wrench size={20} />, label: "Enterprise governance", desc: "Documented policies, SLA-based reporting, audit trails." },
  ];
  return (
    <section className="section bg-white" data-testid="security-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-6">Security & Compliance</div>
          <h2 className="text-[40px] lg:text-[56px] leading-[1.02] tracking-[-0.03em] text-ink font-semibold">
            Enterprise-grade security at every layer.
          </h2>
          <p className="mt-6 text-[16px] text-grey-700 leading-[1.7] max-w-md font-light">
            We protect your data, IP and customers with controls engineered for regulated
            industries — from BFSI to retail to energy.
          </p>
          <div className="mt-8 inline-flex items-center gap-3 px-5 py-3.5 rounded-full bg-coral-tint border border-coral-soft">
            <ShieldCheck size={18} className="text-coral" />
            <span className="font-semibold text-[14px] text-ink">ISO/IEC 27001:2022 Certified</span>
          </div>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-grey-200 border border-grey-200 rounded-2xl overflow-hidden">
          {items.map((it, i) => (
            <div key={i} className="bg-white p-7" data-testid={`security-card-${i}`}>
              <div className="w-10 h-10 rounded-lg bg-coral-tint text-coral flex items-center justify-center mb-5">
                {it.icon}
              </div>
              <h3 className="text-[20px] leading-tight text-ink font-semibold">{it.label}</h3>
              <p className="mt-2 text-[14px] text-grey-600 leading-[1.6] font-light">{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- About ---------- */
function About() {
  return (
    <section id="about" className="section bg-paper" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="eyebrow mb-6">About</div>
          <h2 className="text-[40px] lg:text-[60px] leading-[1.02] tracking-[-0.03em] text-ink font-semibold">
            Powered by BMP Technologies.
          </h2>
          <p className="mt-7 text-[16.5px] text-grey-700 leading-[1.75] font-light">
            TechShore Global is part of <span className="font-semibold text-ink">BMP Tech One Pty Ltd</span> —
            an Australian technology services company with over a decade delivering for enterprise
            clients across financial services, retail, energy and entertainment. Our Hyderabad
            delivery centre extends that capability offshore — secure, scalable, AI-fluent and
            aligned to Australian standards.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-8">
            {[
              { lbl: "HQ", v: "Sydney", sub: "Barangaroo" },
              { lbl: "Delivery", v: "Hyderabad", sub: "India" },
              { lbl: "Coverage", v: "AU & NZ", sub: "Timezone aligned" },
            ].map((x, i) => (
              <div key={i} data-testid={`about-loc-${i + 1}`}>
                <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-2">{x.lbl}</div>
                <div className="text-[18px] text-ink leading-tight font-semibold">{x.v}</div>
                <div className="text-[13px] text-grey-500 mt-1 font-light">{x.sub}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="rounded-2xl overflow-hidden aspect-[5/4] bg-grey-100">
            <img
              src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1600&q=85"
              alt="Modern offshore engineering team workspace — TechShore Global Hyderabad"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Contact ---------- */
function ContactForm() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    team_size: "", service: "", message: "",
  });
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setStatus("error");
      setErrMsg("Please provide your name and email.");
      return;
    }
    setStatus("submitting");
    setErrMsg("");
    try {
      await axios.post(`${API}/consultations`, {
        name: form.name,
        email: form.email,
        company: form.company || null,
        phone: form.phone || null,
        team_size: form.team_size || null,
        service: form.service || null,
        message: form.message || null,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrMsg(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <section id="contact" className="section bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-6">Let's build</div>
          <h2 className="text-[40px] lg:text-[60px] leading-[1.02] tracking-[-0.03em] text-ink font-semibold">
            Set up a world-class AI engineering team.
          </h2>

          <div className="mt-10 space-y-6">
            {[
              { n: "01", t: "Tell us what you need", d: "Fill in your details. 100% confidential. We sign NDAs." },
              { n: "02", t: "Talk to our experts", d: "An advisor will contact you for a 30-min discovery call." },
              { n: "03", t: "Get a tailored proposal", d: "A solution matched to your stack, needs and timeline." },
            ].map((s, i) => (
              <div key={i} className="flex items-start gap-5" data-testid={`process-${i}`}>
                <div className="text-coral text-[42px] leading-none font-light w-12">{s.n}</div>
                <div>
                  <div className="text-[19px] text-ink font-semibold">{s.t}</div>
                  <div className="text-[14.5px] text-grey-600 mt-1 leading-[1.55] font-light">{s.d}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-grey-200 grid grid-cols-2 gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-2">Email</div>
              <a href="mailto:contact@techshoreglobal.com" className="text-ink text-[14.5px] hover:text-coral transition-colors flex items-center gap-2 font-medium">
                <Mail size={14} /> contact@techshoreglobal.com
              </a>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-2">Phone</div>
              <a href="tel:+61280678518" className="text-ink text-[14.5px] hover:text-coral transition-colors flex items-center gap-2 font-medium">
                <Phone size={14} /> 02 8067 8518
              </a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {status === "success" ? (
            <div className="card-flat p-10 text-center" data-testid="contact-success">
              <div className="w-14 h-14 rounded-full bg-coral text-white flex items-center justify-center mx-auto">
                <Check size={28} strokeWidth={3} />
              </div>
              <h4 className="text-[26px] text-ink mt-6 font-semibold">Got it — talk soon.</h4>
              <p className="text-[14.5px] text-grey-600 mt-3 max-w-sm mx-auto leading-[1.6] font-light">
                Your request is in. One of our advisors will reach out within one business day.
              </p>
              <button onClick={() => setStatus("idle")} className="btn-outline mt-7" data-testid="contact-success-reset">
                Send another <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="card-flat p-8 lg:p-10 grid sm:grid-cols-2 gap-5" data-testid="contact-form">
              <div className="sm:col-span-1">
                <label className="field-label">Name *</label>
                <input className="field" value={form.name} onChange={set("name")} placeholder="Jane Smith" required data-testid="form-name" />
              </div>
              <div className="sm:col-span-1">
                <label className="field-label">Email *</label>
                <input type="email" className="field" value={form.email} onChange={set("email")} placeholder="jane@company.com" required data-testid="form-email" />
              </div>
              <div className="sm:col-span-1">
                <label className="field-label">Company</label>
                <input className="field" value={form.company} onChange={set("company")} placeholder="Acme Pty Ltd" data-testid="form-company" />
              </div>
              <div className="sm:col-span-1">
                <label className="field-label">Phone</label>
                <input className="field" value={form.phone} onChange={set("phone")} placeholder="+61 ..." data-testid="form-phone" />
              </div>
              <div className="sm:col-span-1">
                <label className="field-label">Team size</label>
                <select className="field" value={form.team_size} onChange={set("team_size")} data-testid="form-team-size">
                  <option value="">Select</option>
                  <option>1–3 engineers</option>
                  <option>4–10 engineers</option>
                  <option>11–25 engineers</option>
                  <option>25+ engineers</option>
                </select>
              </div>
              <div className="sm:col-span-1">
                <label className="field-label">Capability</label>
                <select className="field" value={form.service} onChange={set("service")} data-testid="form-service">
                  <option value="">Select</option>
                  <option>AI / GenAI engineering</option>
                  <option>Data & ML platform</option>
                  <option>Full-stack engineering</option>
                  <option>Cloud / DevOps</option>
                  <option>Salesforce</option>
                  <option>Not sure yet</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="field-label">What are you trying to build?</label>
                <textarea className="field" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your project, stack and timeline..." data-testid="form-message" />
              </div>

              {status === "error" && (
                <div className="sm:col-span-2 text-[13.5px] text-coral-3 bg-coral-tint border border-coral-soft rounded px-4 py-2.5 font-medium" data-testid="form-error">
                  {errMsg}
                </div>
              )}

              <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                <p className="text-[12px] text-grey-500 max-w-sm leading-[1.5] font-light">
                  Your data is 100% confidential. We sign NDAs.
                </p>
                <button type="submit" disabled={status === "submitting"} className="btn-primary" data-testid="form-submit">
                  {status === "submitting" ? "Sending..." : "Speak with an expert"}
                  <ArrowRight size={16} />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

/* ---------- Footer ---------- */
function Footer() {
  return (
    <footer className="bg-paper border-t border-grey-200" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-12 gap-8 items-start">
        <div className="md:col-span-4">
          <a href="/" className="inline-block mb-4" data-testid="footer-brand">
            <img src={LOGO_IMG} alt="TechShore Global" className="h-14 w-auto" />
          </a>
          <p className="text-[14px] text-grey-600 max-w-md leading-[1.65] font-light">
            AI-led offshore engineering partner for Australian enterprises. Building secure, ISO 27001 certified offshore IT services from Hyderabad, India.
          </p>
          <div className="mt-6 flex items-center gap-3" data-testid="footer-social">
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TechShore Global on LinkedIn"
              className="w-11 h-11 rounded-full bg-ink text-white hover:bg-coral hover:scale-105 transition-all flex items-center justify-center"
              data-testid="footer-linkedin"
            >
              <Linkedin size={18} />
            </a>
            <a
              href={YOUTUBE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TechShore Global on YouTube"
              className="w-11 h-11 rounded-full bg-ink text-white hover:bg-coral hover:scale-105 transition-all flex items-center justify-center"
              data-testid="footer-youtube"
            >
              <Youtube size={18} />
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TechShore Global on Instagram"
              className="w-11 h-11 rounded-full bg-ink text-white hover:bg-coral hover:scale-105 transition-all flex items-center justify-center"
              data-testid="footer-instagram"
            >
              <Instagram size={18} />
            </a>
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-3">Company</div>
          <ul className="space-y-2 text-[14px] text-grey-700 font-light">
            <li><a href="/services" className="hover:text-coral">Services</a></li>
            <li><a href="/crm-solutions" className="hover:text-coral">CRM Solutions</a></li>
            <li><a href="/offshore-teams" className="hover:text-coral">Offshore Teams</a></li>
            <li><a href="/industries" className="hover:text-coral">Industries</a></li>
            <li><a href="/about" className="hover:text-coral">About</a></li>
            <li><a href="/blog" className="hover:text-coral">Blog</a></li>
            <li><a href="/contact" className="hover:text-coral">Contact</a></li>
          </ul>
        </div>
        <div className="md:col-span-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-3">CRM platforms</div>
          <ul className="space-y-2 text-[14px] text-grey-700 font-light">
            <li>Salesforce · Microsoft Dynamics · SAP</li>
            <li>HubSpot · Zoho · Odoo</li>
          </ul>
          <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-3 mt-5">Services</div>
          <ul className="space-y-2 text-[14px] text-grey-700 font-light">
            <li><a href="/services#dedicated" className="hover:text-coral">Dedicated offshore teams</a></li>
            <li><a href="/services#ai" className="hover:text-coral">AI & data engineering</a></li>
            <li><a href="/services#salesforce" className="hover:text-coral">Salesforce support</a></li>
            <li><a href="/services#managed" className="hover:text-coral">Managed IT services</a></li>
          </ul>
        </div>
        <div className="md:col-span-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-3">Sydney HQ</div>
          <div className="text-[14px] text-grey-700 leading-[1.6] flex items-start gap-2 font-light mb-3">
            <MapPin size={14} className="mt-1 flex-shrink-0 text-coral" />
            <span>L24, Three International Towers<br />Barangaroo Avenue, Sydney 2000</span>
          </div>
          <a href="mailto:contact@techshoreglobal.com" className="text-[14px] text-grey-700 hover:text-coral block mb-1.5 font-light">contact@techshoreglobal.com</a>
          <a href="tel:+61280678518" className="text-[14px] text-grey-700 hover:text-coral block font-light">02 8067 8518</a>
        </div>
      </div>
      <div className="border-t border-grey-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[12px] text-grey-500 uppercase tracking-[0.1em] font-medium">
          <div>© {new Date().getFullYear()} TechShore Global · Part of BMP Tech One Pty Ltd · ABN 40 611 334 037</div>
          <div className="flex gap-5">
            <a href="/services" className="hover:text-coral">Services</a>
            <a href="/about" className="hover:text-coral">About</a>
            <a href="/blog" className="hover:text-coral">Blog</a>
            <a href="#contact" className="hover:text-coral">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ---------- Modal ---------- */
function ConsultationModal({ mode, onClose }) {
  const isPricing = mode === "pricing";
  const title = isPricing ? "Request pricing" : "Build your offshore team";
  const subtitle = isPricing
    ? "Tell us a bit about your needs — we'll send across indicative pricing."
    : "Pick a preferred time. A 30-minute call to map your needs.";

  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    team_size: "", service: "",
    preferred_date: "", preferred_time: "", message: "",
  });
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setStatus("error");
      setErrMsg("Please provide your name and email.");
      return;
    }
    setStatus("submitting");
    setErrMsg("");
    try {
      await axios.post(`${API}/consultations`, {
        name: form.name, email: form.email,
        company: form.company || null, phone: form.phone || null,
        team_size: form.team_size || null,
        service: form.service || (isPricing ? "Pricing inquiry" : "General consultation"),
        preferred_date: form.preferred_date || null,
        preferred_time: form.preferred_time || null,
        message: form.message || (isPricing ? "Pricing request" : null),
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrMsg(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="modal-backdrop">
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-coral font-bold">TechShore Global</div>
            <h3 className="text-[26px] text-ink leading-tight mt-1.5 font-semibold" data-testid="modal-title">
              {title}
            </h3>
            <p className="text-[14px] text-grey-600 mt-1.5 font-light">{subtitle}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-grey-100 transition" data-testid="modal-close">
            <X size={20} className="text-ink" />
          </button>
        </div>

        {status === "success" ? (
          <div className="text-center py-10" data-testid="modal-success">
            <div className="w-14 h-14 rounded-full bg-coral text-white flex items-center justify-center mx-auto">
              <Check size={28} strokeWidth={3} />
            </div>
            <h4 className="text-[24px] text-ink mt-5 font-semibold">Got it — talk soon.</h4>
            <p className="text-[14.5px] text-grey-600 mt-2 max-w-sm mx-auto leading-[1.6] font-light">
              We've received your request. An advisor will reach out within one business day.
            </p>
            <button onClick={onClose} className="btn-primary mt-7" data-testid="modal-success-close">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="modal-form">
            <div className="sm:col-span-1">
              <label className="field-label">Name *</label>
              <input className="field" value={form.name} onChange={set("name")} placeholder="Jane Smith" required data-testid="form-name" />
            </div>
            <div className="sm:col-span-1">
              <label className="field-label">Email *</label>
              <input type="email" className="field" value={form.email} onChange={set("email")} placeholder="jane@company.com" required data-testid="form-email" />
            </div>
            <div className="sm:col-span-1">
              <label className="field-label">Company</label>
              <input className="field" value={form.company} onChange={set("company")} placeholder="Acme Pty Ltd" data-testid="form-company" />
            </div>
            <div className="sm:col-span-1">
              <label className="field-label">Phone</label>
              <input className="field" value={form.phone} onChange={set("phone")} placeholder="+61 ..." data-testid="form-phone" />
            </div>
            <div className="sm:col-span-1">
              <label className="field-label">Team size</label>
              <select className="field" value={form.team_size} onChange={set("team_size")} data-testid="form-team-size">
                <option value="">Select</option>
                <option>1–3 engineers</option>
                <option>4–10 engineers</option>
                <option>11–25 engineers</option>
                <option>25+ engineers</option>
              </select>
            </div>
            <div className="sm:col-span-1">
              <label className="field-label">Capability</label>
              <select className="field" value={form.service} onChange={set("service")} data-testid="form-service">
                <option value="">Select</option>
                <option>AI / GenAI engineering</option>
                <option>Data & ML platform</option>
                <option>Full-stack engineering</option>
                <option>Cloud / DevOps</option>
                <option>Salesforce</option>
                <option>Not sure yet</option>
              </select>
            </div>
            {!isPricing && (
              <>
                <div className="sm:col-span-1">
                  <label className="field-label"><CalendarDays size={11} className="inline mr-1" />Preferred date</label>
                  <input type="date" className="field" value={form.preferred_date} onChange={set("preferred_date")} data-testid="form-date" />
                </div>
                <div className="sm:col-span-1">
                  <label className="field-label"><Clock size={11} className="inline mr-1" />Preferred time</label>
                  <select className="field" value={form.preferred_time} onChange={set("preferred_time")} data-testid="form-time">
                    <option value="">Select</option>
                    <option>9:00 AM AEST</option>
                    <option>11:00 AM AEST</option>
                    <option>1:00 PM AEST</option>
                    <option>3:00 PM AEST</option>
                    <option>5:00 PM AEST</option>
                  </select>
                </div>
              </>
            )}
            <div className="sm:col-span-2">
              <label className="field-label">Message</label>
              <textarea className="field" rows={3} value={form.message} onChange={set("message")} placeholder="Anything we should know?" data-testid="form-notes" />
            </div>

            {status === "error" && (
              <div className="sm:col-span-2 text-[13.5px] text-coral-3 bg-coral-tint border border-coral-soft rounded px-4 py-2.5 font-medium" data-testid="form-error">
                {errMsg}
              </div>
            )}

            <div className="sm:col-span-2 flex items-center justify-between gap-4 mt-2">
              <p className="text-[12px] text-grey-500 font-light">Confidential. We sign NDAs.</p>
              <button type="submit" disabled={status === "submitting"} className="btn-primary" data-testid="form-submit">
                {status === "submitting" ? "Sending..." : isPricing ? "Request pricing" : "Build your offshore team"}
                <ArrowRight size={16} />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ---------- App ---------- */
function App() {
  const [modal, setModal] = useState(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in")),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  useEffect(() => { axios.get(`${API}/health`).catch(() => {}); }, []);

  useEffect(() => {
    document.body.style.overflow = modal ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [modal]);

  return (
    <div className="App" data-testid="app-root">
      <Nav openModal={setModal} />
      <Hero openModal={setModal} />
      <TrustBar />
      <CRMSpecialisation />
      <CoreServices openModal={setModal} />
      <Industries />
      <ProofStrip />
      <Approach />
      <MidCTA openModal={setModal} />
      <EngagementModels />
      <AICapability />
      <Results />
      <CyberabadShowcase />
      <Security />
      <About />
      <ContactForm />
      <Footer />

      {modal && <ConsultationModal mode={modal} onClose={() => setModal(null)} />}
    </div>
  );
}

export default App;
